import {
  call,
  put,
  takeLatest,
  takeEvery
} from "redux-saga/effects";
import {
  schema
} from "normalizr";
import {
  authorizedRequest,
  downloadFile
} from "utils/api";
import {
  ToastSuccess,
  ToastError
} from "components/Global/Toasts/Toasty";
import {
  defaultReturn,
  makePayload,
  generateDefaultActions,
  requestSaga,
  handleCallback,
  initialState,
  makeReadPayload,
  makeWritePayload,
  fillDictionary
} from "shared-utility/store/utils/helpers";

export const CONTACTS_KEY = "contacts";
export const CONTACTS_ENTITY = new schema.Entity(CONTACTS_KEY, {});
export const CONTACTS_SCHEMA = [CONTACTS_ENTITY];

export const GET_CONTACTS = generateDefaultActions("GET_CONTACTS");
export const GET_CONTACTS_BY_COMPANY = generateDefaultActions("GET_CONTACTS_BY_COMPANY");
export const GET_CONTACT = generateDefaultActions("GET_CONTACT");
export const CREATE_CONTACT = generateDefaultActions("CREATE_CONTACT");
export const UPDATE_CONTACT = generateDefaultActions("UPDATE_CONTACT");
export const CONTACT_CHANGE_PASS = generateDefaultActions("CONTACT_CHANGE_PASS");
export const CONTACT_RESET_PASS = generateDefaultActions("CONTACT_RESET_PASS");
export const DOWNLOAD_CONTACTS = generateDefaultActions("DOWNLOAD_CONTACTS");
export const SHOULD_REFRESH = "SHOULD_REFRESH";
export const DELETE_CONTACT = generateDefaultActions("DELETE_CONTACT");

export default function contactsReducer(state = initialState, action) {
  const {
    type,
    payload
  } = action;
  switch (type) {
    case GET_CONTACTS.success:
      return defaultReturn(state, payload, "contacts");
    case GET_CONTACTS_BY_COMPANY.success:
      // eslint-disable-next-line no-case-declarations
      const key = Object.keys(payload)[0];

      return fillDictionary(state, payload[key], "contactsByCompany", key);
    case GET_CONTACT.success:
      return defaultReturn(state, payload, "contact");

    case CREATE_CONTACT.success:
      ToastSuccess("Save successful");

      return defaultReturn(state, payload, "contact");

    case UPDATE_CONTACT.success:
      ToastSuccess("Save successful");

      return defaultReturn(state, payload, "contact");

    case DELETE_CONTACT.success:
      ToastSuccess("Save successful");

      return defaultReturn(state, payload, "contact");

    case CONTACT_CHANGE_PASS:
      return defaultReturn(state, payload, "contact");
    case CONTACT_RESET_PASS:
      return defaultReturn(state, payload, "contact");

    case CONTACTS_ACCEPT_AGREEMENTS.success:
      ToastSuccess("Agreement Accepted");

      return defaultReturn(state, payload.accepted, "agreementAccepted");

    case CHECK_IF_EXISTING_EMAIL.success:
      return defaultReturn(state, payload.emailExists, "emailExists");

    case CHECK_IF_EXISTING_EMAIL.failure:
      ToastError("Email Duplicate Check Failed");

      return defaultReturn(state, null, "emailExists");
    case SHOULD_REFRESH:
      return {
        ...state,
        shouldRefresh: payload
      };
    case DOWNLOAD_CONTACTS:
      return defaultReturn(state, payload, "");

    default:
      return state;
  }
}

// GET Contacts
export const getContacts = payload => makeReadPayload(GET_CONTACTS.value, payload);

export const getContactsByCompany = payload => makePayload(GET_CONTACTS_BY_COMPANY.value, payload);

// GET Contact
export const getContact = payload => makeReadPayload(GET_CONTACT.value, payload);

// CREATE Contact
export const createContact = payload => makeWritePayload(CREATE_CONTACT.value, payload);

// UPDATE Contact
export const updateContact = payload => makeWritePayload(UPDATE_CONTACT.value, payload);

export const changePassword = payload => makeWritePayload(CONTACT_CHANGE_PASS.value, payload);

export const resetPassword = payload => makeWritePayload(CONTACT_RESET_PASS.value, payload);

export const setShouldRefresh = payload => makePayload(SHOULD_REFRESH, payload);

export const downloadContacts = payload => makePayload(DOWNLOAD_CONTACTS.value, payload);

export const deleteContact = payload => makePayload(DELETE_CONTACT.value, { payload });
function* deleteContactSaga(payload) {
  try {
    yield call(authorizedRequest, `/contact/${payload.payload}`, "DELETE");
    yield put({ type: SHOULD_REFRESH, payload: { shouldRefresh: true } });
  } catch (error) {
    yield put(DELETE_CONTACT.createFailure(error));
  }
}

export const CONTACTS_ACCEPT_AGREEMENTS = generateDefaultActions("CONTACTS_ACCEPT_AGREEMENTS");
export const acceptAgreements = payload => makePayload(CONTACTS_ACCEPT_AGREEMENTS.value, payload);
function* acceptAgreementsSaga(payload) {
  const callback = handleCallback(payload);
  const { userId } = payload;
  const url = `/acceptAgreement/${userId}`;
  yield requestSaga(CONTACTS_ACCEPT_AGREEMENTS, url, "POST", payload);
  yield callback();
}

export const CHECK_IF_EXISTING_EMAIL = generateDefaultActions("CHECK_IF_EXISTING_EMAIL");
export const checkForExistingContactEmail = payload => makePayload(CHECK_IF_EXISTING_EMAIL.value, payload);
function* checkForExistingContactEmailSaga(payload) {
  const { email } = payload;
  const callback = handleCallback(payload);
  const url = "/contact/checkForExistingEmail";
  yield requestSaga(CHECK_IF_EXISTING_EMAIL, url, "POST", { email });
  yield callback();
}
// Saga GET Paginated Contacts
function* getPaginatedContactsSaga(payload) {
  let url = "/contacts";
  const {
    page,
    limit,
    orderDir,
    orderBy,
    searchText
  } = payload;
  // Add pagination params
  if (page !== null && limit !== null) {
    url = `${url}?page=${page + 1}&limit=${limit}`;
  }

  // Add sorting params
  if (orderDir && orderBy) {
    url = `${url}&orderField=${orderBy}&orderDir=${orderDir}`;
  }

  if (searchText) {
    url = `${url}&searchText=${searchText}`;
  }

  yield requestSaga(GET_CONTACTS, url);
}

// Saga GET Contact
function* getContactSaga(payload) {
  yield requestSaga(GET_CONTACT, `/contact/${payload.id}`);
}

function* getContactsByCompanySaga({ companyId }) {
  const url = `/contactsByCompany?companyId=${companyId}`;

  yield requestSaga(GET_CONTACTS_BY_COMPANY, url);
}

// Saga CREATE Contact
function* createContactSaga(payload) {
  try {
    const response = yield call(authorizedRequest, "/contacts", "POST", {
      contact: payload.payload
    });
    yield put(CREATE_CONTACT.createSuccess(response));
    yield put({
      type: SHOULD_REFRESH,
      payload: {
        shouldRefresh: true
      }
    });
  } catch (error) {
    yield put(CREATE_CONTACT.createFailure(error));
    ToastError("Unable to save user. Contact the LoanBoss Administrator.");
  }
}

// Saga UPDATE Contact
function* updateContactSaga(payload) {
  try {
    const response = yield call(
      authorizedRequest,
      `/contact/${payload.payload.id}`,
      "PUT",
      {
        contact: payload.payload
      }
    );
    yield put(UPDATE_CONTACT.createSuccess(response));
    yield put({
      type: SHOULD_REFRESH,
      payload: {
        shouldRefresh: true
      }
    });
    // yield put(getContacts());
  } catch (error) {
    yield put(UPDATE_CONTACT.createFailure(error));
  }
}

function* changePassSaga(action) {
  try {
    const {
      payload
    } = action;
    const response = yield call(
      authorizedRequest,
      `/users/${payload.id}/changepassword`,
      "POST",
      payload.payload
    );
    yield put(CONTACT_CHANGE_PASS.createSuccess(response));
    ToastSuccess(response.success === false ? response.msg : "Password updated");
  } catch (error) {
    yield put(CONTACT_CHANGE_PASS.createFailure(error));
    ToastError(error.message);
  }
}

function* resetPassSaga(action) {
  try {
    const {
      payload
    } = action;
    const response = yield call(
      authorizedRequest,
      `/users/${payload.id}/resetpassword`,
      "POST",
      payload.payload
    );
    yield put(CONTACT_RESET_PASS.createSuccess(response));
    ToastSuccess(response.success === false ? response.msg : "Email has been sent to the user");
  } catch (error) {
    yield put(CONTACT_RESET_PASS.createFailure(error));
    ToastError(error.message);
  }
}

function* downloadContactsSaga(payload) {
  try {
    yield call(downloadFile, "contactsdownload", "GET");
  } catch (error) {
    yield put(DOWNLOAD_CONTACTS.createFailure(error));
  }
}

export function* contactsSaga() {
  yield takeLatest(GET_CONTACTS.value, getPaginatedContactsSaga);
  yield takeLatest(GET_CONTACT.value, getContactSaga);
  yield takeEvery(GET_CONTACTS_BY_COMPANY.value, getContactsByCompanySaga);
  yield takeLatest(CREATE_CONTACT.value, createContactSaga);
  yield takeLatest(UPDATE_CONTACT.value, updateContactSaga);
  yield takeLatest(CONTACT_CHANGE_PASS.value, changePassSaga);
  yield takeLatest(CONTACT_RESET_PASS.value, resetPassSaga);
  yield takeLatest(DOWNLOAD_CONTACTS.value, downloadContactsSaga);
  yield takeLatest(DELETE_CONTACT.value, deleteContactSaga);
  yield takeLatest(CONTACTS_ACCEPT_AGREEMENTS.value, acceptAgreementsSaga);
  yield takeLatest(CHECK_IF_EXISTING_EMAIL.value, checkForExistingContactEmailSaga);
}
